import React from 'react';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { CircuitDrawerV2 } from './CircuitDrawerV2';
import { toast } from 'sonner';
import { useAppStore } from '@/stores/appStore';
import { useOptimisticCircuitMutation } from '@/hooks/mutations/useOptimisticCircuitMutation';
import { logger } from '@/lib/logger';

interface CircuitCreationHandlerOptimisticProps {
  circuitDrawerOpen: boolean;
  setCircuitDrawerOpen: (open: boolean) => void;
  exitCircuitMode: () => void;
  isMobile: boolean;
  newCircuitName: string;
  setNewCircuitName: (name: string) => void;
  floorPlanId: string;
  useOptimistic?: boolean; // Feature flag for gradual rollout
}

export function CircuitCreationHandlerOptimistic({
  circuitDrawerOpen,
  setCircuitDrawerOpen,
  exitCircuitMode,
  isMobile,
  newCircuitName,
  setNewCircuitName,
  floorPlanId,
  useOptimistic = true // Default to true, but can be disabled
}: CircuitCreationHandlerOptimisticProps) {
  const { data } = useFloorPlanContext();
  const selectedFixtures = useAppStore((state) => state.selection.fixtures);
  const clearAllSelections = useAppStore((state) => state.clearAllSelections);
  
  const optimisticMutation = useOptimisticCircuitMutation();

  const handleCreateCircuitWithFixtures = async () => {
    if (!newCircuitName.trim()) {
      toast.error('Please enter a load name');
      return;
    }
    
    if (selectedFixtures.size === 0) {
      toast.error('Please select at least one fixture');
      return;
    }
    
    const fixtureIds = Array.from(selectedFixtures);
    const loadName = newCircuitName.trim();
    
    // Clear selections and reset immediately for rapid creation
    clearAllSelections();
    setNewCircuitName('');
    
    try {
      if (useOptimistic) {
        // Use optimistic mutation - fire and forget
        logger.log('Using optimistic circuit creation');
        optimisticMutation.mutate({
          floorPlanId,
          name: loadName,
          fixtureIds,
          loadType: 'lighting',
          zone: ''
        });
        // Don't await - let it run in background
      } else {
        // Fallback to original implementation
        logger.log('Using standard circuit creation');
        const { createCircuitGroup, assignFixtureToCircuit } = await import('@/services/floorPlanService');
        
        // Run in background for non-optimistic too
        (async () => {
          try {
            const { data: newCircuit, error } = await createCircuitGroup(
              floorPlanId,
              loadName,
              'lighting',
              '',
              undefined
            );
            
            if (error || !newCircuit) {
              throw error || new Error('Failed to create load');
            }
            
            // Assign each fixture to the circuit
            for (const fixtureId of fixtureIds) {
              await assignFixtureToCircuit(fixtureId, newCircuit.id);
            }
            
            // Refetch data to update the UI
            await data.refetch();
            
            toast.success('Load Successfully Created');
          } catch (error) {
            logger.error('Failed to create circuit:', error);
            toast.error('Failed to create load');
          }
        })();
      }
      
      // Don't exit circuit mode - keep the drawer open
      // The user can continue creating more circuits or press ESC to exit
    } catch (error) {
      if (!useOptimistic) {
        logger.error('Failed to create circuit:', error);
        toast.error('Failed to create load');
      }
      // If using optimistic updates, error handling is done in the mutation
    }
  };

  return (
    <CircuitDrawerV2
      open={circuitDrawerOpen}
      onOpenChange={(open) => {
        if (!open) {
          exitCircuitMode();
        }
      }}
      isMobile={isMobile}
      newCircuitName={newCircuitName}
      setNewCircuitName={setNewCircuitName}
      handleCreateCircuitWithFixtures={handleCreateCircuitWithFixtures}
      exitCircuitMode={exitCircuitMode}
      isPending={optimisticMutation.isPending}
    />
  );
}